import { useMutation } from '@apollo/client'
import {
  faBuilding,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faMapLocationDot,
  faTrash,
  faWrench
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAtom } from 'jotai'
import { useState, useEffect } from 'react'

import {
  UserRole,
  type Customer,
  type Location
} from '@/graphql/generated-types'
import { DELETE_DEVICE, UPDATE_DEVICE } from '@/graphql/mutations'
import { customersAtom } from '@/shared-state/customers'
import { sessionAtom } from '@/shared-state/session'
import { log, logError } from '@/utils/log'

import Modal from './Modal'

function SelectCustomer({
  customer,
  setCustomer
}: {
  customer: Customer
  setCustomer: (customer: Customer) => void
}) {
  return (
    <button
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors text-left mb-2"
      onClick={() => setCustomer(customer)}
    >
      <span className="flex items-center justify-center size-9 rounded-lg bg-primary/10 text-primary shrink-0">
        <FontAwesomeIcon icon={faBuilding} />
      </span>
      <span className="flex-1 font-medium">{customer.name}</span>
      <FontAwesomeIcon icon={faChevronRight} className="opacity-30 text-sm" />
    </button>
  )
}

function SelectLocation({
  location,
  setLocation
}: {
  location: Location
  setLocation: (locationId: string) => void
}) {
  return (
    <button
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors text-left mb-2"
      onClick={() => setLocation(location.locationId)}
    >
      <span className="flex items-center justify-center size-9 rounded-lg bg-primary/10 text-primary shrink-0">
        <FontAwesomeIcon icon={faMapLocationDot} />
      </span>
      <span className="flex-1 font-medium">{location.name}</span>
      <FontAwesomeIcon icon={faChevronRight} className="opacity-30 text-sm" />
    </button>
  )
}

export default function InstallDevice({ customerId }: { customerId: string }) {
  const [customers] = useAtom(customersAtom)
  const [session] = useAtom(sessionAtom)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [installDeviceId, setInstallDeviceId] = useState<string | null>(null)
  const [showToast, setShowToast] = useState(false)
  const locations = customer?.locations || []

  const [deleteDevice] = useMutation(DELETE_DEVICE)
  const [updateDevice] = useMutation(UPDATE_DEVICE)

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showToast])

  const detachedDevices =
    customers.find(c => c.customerId === customerId)?.detachedDevices || []

  const canSeeDetachedDevices =
    session?.role === UserRole.Admin ||
    session?.role === UserRole.Customer ||
    session?.role === UserRole.LocationAdmin

  if (
    !detachedDevices ||
    detachedDevices.length === 0 ||
    !canSeeDetachedDevices
  )
    return null

  function startDeviceInstall(deviceId: string) {
    log('installDevice', deviceId)
    setInstallDeviceId(deviceId)
  }

  async function installDeviceAtLocation(locationId: string) {
    log('installDevice', customerId, locationId, installDeviceId)
    const name = prompt('Enter a name for the device', 'My Device')
    if (!name || name.length === 0) return
    try {
      await updateDevice({
        variables: {
          input: {
            deviceId: installDeviceId,
            locationId: locationId,
            name: name
          }
        }
      })
      window.location.reload()
    } catch (err) {
      logError('Update device failed:', err)
    }
    setInstallDeviceId(null)
    setCustomer(null)
  }

  async function confirmDeleteDevice() {
    //if (!confirm('Are you sure you want to delete this device?')) return
    try {
      await deleteDevice({
        variables: {
          deviceId: installDeviceId
        }
      })
      setShowToast(true)
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (err) {
      logError('Delete device failed:', err)
    }
  }

  return (
    <div>
      <div className="mb-3">
        {detachedDevices.map(device => (
          <div
            key={device.deviceId}
            className="card card-border bg-base-100 shadow-md border-l-4 border-l-success border-base-300 card-sm mt-2"
          >
            <div className="card-body flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="relative flex size-3 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75"></span>
                  <span className="relative inline-flex size-3 rounded-full bg-success"></span>
                </span>
                <div className="font-semibold text-sm">
                  {device.name} found!
                </div>
              </div>
              <button
                className="btn btn-sm"
                onClick={() => startDeviceInstall(device.deviceId)}
              >
                <FontAwesomeIcon icon={faWrench} /> Setup
              </button>
            </div>
          </div>
        ))}
      </div>

      {installDeviceId && (
        <Modal
          showWrapper
          showCloseButton
          openModal={true}
          closeModal={() => {
            setInstallDeviceId(null)
            setCustomer(null)
          }}
          customClass="shadow-lg"
        >
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            <span
              className={`flex items-center justify-center size-6 rounded-full text-xs font-bold ${!customer ? 'bg-primary text-primary-content' : 'bg-base-300 opacity-40'}`}
            >
              1
            </span>
            <span
              className={`text-xs font-medium ${!customer ? '' : 'opacity-40'}`}
            >
              Customer
            </span>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="opacity-20 text-xs"
            />
            <span
              className={`flex items-center justify-center size-6 rounded-full text-xs font-bold ${customer ? 'bg-primary text-primary-content' : 'bg-base-300 opacity-40'}`}
            >
              2
            </span>
            <span
              className={`text-xs font-medium ${customer ? '' : 'opacity-40'}`}
            >
              Location
            </span>
          </div>

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            {customer && (
              <button
                className="btn btn-ghost btn-sm btn-square"
                onClick={() => setCustomer(null)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            <h2 className="card-title spotonFont text-3xl">
              {customer ? 'Select location' : 'Select customer'}
            </h2>
          </div>
          {customer && (
            <p className="text-sm opacity-50 mb-4 ml-10">{customer.name}</p>
          )}

          {/* Lists */}
          {!customer && (
            <div>
              {customers.map(customer => (
                <SelectCustomer
                  key={customer.customerId}
                  customer={customer}
                  setCustomer={setCustomer}
                />
              ))}
            </div>
          )}
          {customer && (
            <div>
              {locations.map(location => (
                <SelectLocation
                  key={location.locationId}
                  location={location}
                  setLocation={installDeviceAtLocation}
                />
              ))}
            </div>
          )}

          {/* Delete / success */}
          <div className="flex flex-col items-center gap-4 mt-6 pt-4 border-t border-base-300">
            {showToast ? (
              <div className="alert alert-success shadow-md w-full">
                <FontAwesomeIcon icon={faCheck} />
                <span>Device deleted successfully</span>
              </div>
            ) : (
              <button
                className="btn btn-ghost btn-sm text-error/70 hover:text-error hover:bg-error/10"
                onClick={confirmDeleteDevice}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete device
              </button>
            )}
          </div>

          {/* Device ID — subtle footer */}
          <p className="text-center text-[10px] font-mono opacity-20 mt-4 break-all">
            {installDeviceId}
          </p>
        </Modal>
      )}
    </div>
  )
}
